import { pathAsArray, pathAsString } from './shared';

/**
 * Ensure an object structure exists that fulfills the given path.
 *
 * @param {object} target The target object to search
 * @param {string} path The path to search
 */
export const createPathIfNotExists = (target: any, path: any) => {
  if (target === null || target === undefined)
    throw new Error(
      `Attempted to create a path on an empty target. Check how you are using object paths. Path was "${path}".`
    );

  const pathArr = pathAsArray(path) as any;
  const [head, ...tail] = pathArr;

  if (tail.length > 0) {
    if (target[head] === undefined) target[head] = isNaN(tail[0]) ? {} : [];
    createPathIfNotExists(target[head], tail);
  }
};

/**
 * Make multiple set()'s on targetObject using another objects key-value pairs (modificationGroup)
 * @param {object} target The target object to have properties set on.
 * @param {string} path The (dot seperated string) path relative to the targetObject to set a value for.
 * @param {object} valuesObject An object where keys are used as partial paths, and values are set on the path 'modificationPath + key'
 * @param {bool} createPath Whether or not missing object path should be created if missing.
 */
export const setMultiple = (target: any, path: any, valuesObject: any, createPath: boolean = false) => {
  if (typeof valuesObject !== 'object') {
    throw new Error(
      `Attempted to setMultiple using a non-object (${JSON.stringify(valuesObject)}) for property updates, ` +
        `expected an object using immutableObjectPath.`
    );
  }

  const pathStr = pathAsString(path);
  Object.keys(valuesObject).forEach((key) => set(target, `${pathStr}.${key}`, valuesObject[key], createPath));
};

/**
 * Set a value on an object at the provided path.
 *
 * @param {object} target The target object that will be modified
 * @param {string} path The path to search
 * @param {*} value The value to be apploed to targetObject at targetObject
 * @param {bool} createPath Whether or not missing object path should be created if missing.
 */
export const set = (target: any, path: any, value: any, createPath: boolean = false) => {
  const pathArr = pathAsArray(path);

  if (createPath) {
    createPathIfNotExists(target, pathArr);
  }

  const [head, ...tail] = pathArr;

  if (tail.length > 0) {
    set(target[head], tail, value);
  } else {
    target[head] = value;
  }
};

/**
 * Delete a value on an object at the provided path.
 *
 * @param {object} target The target object that will be modified
 * @param {string} path The path to search for deletion
 */
export const del = (target: any, path: any) => {
  if (!target) return;

  const pathArr = pathAsArray(path) as any;
  const [head, ...tail] = pathArr;

  if (tail.length > 0) {
    del(target[head], tail);
  } else {
    if (Array.isArray(target)) {
      const index = parseInt(head, 10);
      target.splice(index, 1);
    } else {
      delete target[head];
    }
  }
};
